import { useCallback, useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { SESSION_POLL_MS, assistError } from "../lib/assist";
import {
  NOTE_FIELDS, NOTE_SAVE_DELAY_MS, afterSave, fieldFromServer, hasUnsaved, isDirty, keepMine, mergeField, takeTheirs,
} from "../lib/assistNotes";
import useVisiblePolling from "./useVisiblePolling";

const emptyFields = () => Object.fromEntries(NOTE_FIELDS.map(({ key }) => [key, fieldFromServer()]));

// The shared note of one assistance (`lib/assistNotes.js` for the rules). One instance per page: the column, the full-screen panel,
// the phone sheet and the report section after the session all show these same fields, so switching between them loses nothing.
// Each field saves on its own, about a second after the last keystroke and when it loses focus, one request at a time; the note
// is re-read every few seconds so a colleague's text appears without reloading.
export default function useAssistNotes(requestId, { canEdit, poll = true }) {
  const [phase, setPhase] = useState("loading"); // loading | ready | error
  const [note, setNote] = useState(null);
  const [fields, setFieldsState] = useState(emptyFields);
  const [status, setStatus] = useState({}); // field -> saving | saved | error
  const fieldsRef = useRef(fields);
  const busy = useRef({});
  const again = useRef({});
  const timers = useRef({});

  const setFields = useCallback((updater) => {
    fieldsRef.current = updater(fieldsRef.current);
    setFieldsState(fieldsRef.current);
  }, []);
  const setField = useCallback((key, updater) => setFields((all) => ({ ...all, [key]: updater(all[key]) })), [setFields]);
  const setFieldStatus = (key, value) => setStatus((all) => ({ ...all, [key]: value }));

  const load = useCallback(async ({ silent = false } = {}) => {
    try {
      const { data } = await api.get(`/assist/requests/${requestId}/notes`);
      setNote(data);
      setFields((all) => Object.fromEntries(NOTE_FIELDS.map(({ key }) => [
        key, mergeField(all[key], data.fields?.[key], { busy: Boolean(busy.current[key]) })])));
      setPhase("ready");
    } catch {
      if (!silent) setPhase("error");
    }
  }, [requestId, setFields]);
  useEffect(() => { load(); }, [load]);
  useVisiblePolling(() => load({ silent: true }), SESSION_POLL_MS, poll && phase === "ready");

  const save = useCallback(async (key) => {
    clearTimeout(timers.current[key]);
    const field = fieldsRef.current[key];
    if (!canEdit || field.conflict || !isDirty(field)) return;
    if (busy.current[key]) {
      again.current[key] = true;
      return;
    }
    busy.current[key] = true;
    setFieldStatus(key, "saving");
    let succeeded = false;
    try {
      const { data } = await api.put(`/assist/requests/${requestId}/notes/${key}`,
        { value: field.value, expected_version: field.version });
      setNote(data);
      setField(key, (local) => afterSave(local, data.fields[key]));
      setFieldStatus(key, "saved");
      succeeded = true;
    } catch (error) {
      const detail = error?.response?.data?.detail;
      if (assistError(error).code === "version_conflict" && detail?.field) {
        setField(key, (local) => ({ ...local, conflict: detail.field }));
        setFieldStatus(key, null);
      } else {
        setFieldStatus(key, "error");
      }
    } finally {
      busy.current[key] = false;
      const more = again.current[key];
      again.current[key] = false;
      if (more || (succeeded && isDirty(fieldsRef.current[key]))) save(key);
    }
  }, [canEdit, requestId, setField]);

  const update = useCallback((key, value) => {
    setField(key, (local) => ({ ...local, value }));
    setFieldStatus(key, null);
    clearTimeout(timers.current[key]);
    timers.current[key] = setTimeout(() => save(key), NOTE_SAVE_DELAY_MS);
  }, [save, setField]);

  const resolve = useCallback((key, keep) => {
    setField(key, keep ? keepMine : takeTheirs);
    if (keep) save(key);
  }, [save, setField]);

  // Leaving with something not yet saved: the browser asks first; leaving the page within the CRM saves what is pending.
  useEffect(() => {
    const onBeforeUnload = (event) => {
      if (!hasUnsaved(fieldsRef.current) && !Object.values(busy.current).some(Boolean)) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    const pending = timers.current;
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      Object.keys(pending).forEach((key) => { clearTimeout(pending[key]); save(key); });
    };
  }, [save]);

  return {
    phase, note, fields, status, canEdit,
    update,
    flush: save,
    retry: save,
    keepMine: (key) => resolve(key, true),
    takeTheirs: (key) => resolve(key, false),
    reload: () => load(),
  };
}
